import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, ButtonGroup, MenuItem, MenuList, Popover } from '@mui/material';
import { BasePreset } from 'interfaces/basePreset';
import React, { useState } from 'react';
import { useAppSelector } from 'redux/hooks';

export interface PresetPermissions {
  canEditDefaultPreset?: boolean;
  canEditOthersPresets?: boolean;
  canDeleteOthersPreset?: boolean;
}
interface Props {
  defaultPreset?: Partial<BasePreset>;
  permissions?: PresetPermissions;
  selectedPreset: Partial<BasePreset>;
  isLoading?: boolean;
  onUpdatePreset?: () => void;
  onSavePresetVersion?: () => void;
  onDeletePreset?: () => void;
  onUpdateDefaultPreset?: () => void;
  onTogglePublished?: () => void;
  onToggleGlobal?: () => void;
  setIsAddingPreset: (isAddingPreset: boolean) => void;
  updateLabel?: string;
  createLabel?: string;
  saveVersionLabel?: string;
  setAsDefaultLabel?: string;
  publishedLabel?: string;
  globalLabel?: string;
  saveDisabled?: boolean;
}

const PresetActionButtons: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  defaultPreset,
  permissions = { canDeleteOthersPreset: false, canEditDefaultPreset: false, canEditOthersPresets: false },
  selectedPreset,
  isLoading,
  onUpdatePreset,
  onSavePresetVersion,
  onDeletePreset,
  onUpdateDefaultPreset,
  onTogglePublished,
  onToggleGlobal,
  setIsAddingPreset,
  updateLabel = 'Update',
  setAsDefaultLabel = 'Set as default',
  createLabel = 'Save as...',
  saveVersionLabel = 'Save as version',
  publishedLabel = 'Publish',
  globalLabel = 'Make global',
  saveDisabled = false,
}) => {
  const { name: userName, userId } = useAppSelector((state) => state.auth.profile) || {};
  const [anchorActionListElement, setAnchorActionListElement] = useState<HTMLButtonElement | null>(null);

  const canUpdatePreset =
    Boolean(onUpdatePreset) &&
    (userName === selectedPreset?.createdBy ||
      userId === selectedPreset?.createdBy ||
      permissions.canEditOthersPresets);
  const canSavePresetVersion = Boolean(onSavePresetVersion);
  const isUpdateMode = canUpdatePreset || canSavePresetVersion;

  const userHavePermissionToDeletePreset = () => {
    return (
      selectedPreset?.id &&
      selectedPreset?.id !== defaultPreset?.id &&
      (selectedPreset?.createdBy === userName ||
        selectedPreset?.createdBy === userId ||
        permissions?.canDeleteOthersPreset)
    );
  };

  const primarySaveAction = (canUpdatePreset ? onUpdatePreset : onSavePresetVersion)
    ? () => {
        (canUpdatePreset ? onUpdatePreset : onSavePresetVersion)?.();
      }
    : undefined;

  return (
    <>
      <ButtonGroup aria-label="split button" size="small" variant="contained" color="primary">
        <Button
          disabled={isLoading || saveDisabled}
          onClick={() => {
            if (isUpdateMode) {
              if (!primarySaveAction) {
                console.error('No preset save function provided');
                return;
              }
              primarySaveAction();
            } else {
              setIsAddingPreset(true);
            }
          }}
        >
          {isUpdateMode ? (canUpdatePreset ? updateLabel : saveVersionLabel) : createLabel}
        </Button>
        {isUpdateMode && (
          <Button
            aria-haspopup="menu"
            aria-label="Open preset actions"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setAnchorActionListElement(event.currentTarget);
            }}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>
      <Popover
        open={Boolean(anchorActionListElement) && !isLoading}
        anchorEl={anchorActionListElement}
        onClose={() => {
          setAnchorActionListElement(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuList id="split-button-menu" autoFocusItem>
          <MenuItem
            key={'Create new preset'}
            onClick={() => {
              setIsAddingPreset(true);
              setAnchorActionListElement(null);
            }}
          >
            {createLabel}
          </MenuItem>
          {canUpdatePreset && onSavePresetVersion && (
            <MenuItem
              key="Save as version"
              disabled={saveDisabled}
              onClick={() => {
                onSavePresetVersion();
                setAnchorActionListElement(null);
              }}
            >
              {saveVersionLabel}
            </MenuItem>
          )}
          {onDeletePreset && (
            <MenuItem
              key={'Delete preset'}
              onClick={() => {
                onDeletePreset();
                setAnchorActionListElement(null);
              }}
              disabled={!userHavePermissionToDeletePreset()}
            >
              Delete preset
            </MenuItem>
          )}
          {onUpdateDefaultPreset && permissions?.canEditDefaultPreset && (
            <MenuItem
              key={'Set as default'}
              onClick={() => {
                onUpdateDefaultPreset();
                setAnchorActionListElement(null);
              }}
            >
              {setAsDefaultLabel}
            </MenuItem>
          )}
          {onTogglePublished && (
            <MenuItem
              key="Toggle published"
              onClick={() => {
                onTogglePublished();
                setAnchorActionListElement(null);
              }}
            >
              {publishedLabel}
            </MenuItem>
          )}
          {onToggleGlobal && (
            <MenuItem
              key="Toggle global"
              onClick={() => {
                onToggleGlobal();
                setAnchorActionListElement(null);
              }}
            >
              {globalLabel}
            </MenuItem>
          )}
        </MenuList>
      </Popover>
    </>
  );
};

export default PresetActionButtons;
